import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { timer } from 'rxjs';
import { tap } from 'rxjs/operators';
import { tryGetErrorMessage } from '../../../error.utils';
import { NotifyService } from '../../../service/notify.service';
import { PanoptycDataService } from '../../../service/panoptyc-data.service';
import { PosthogService } from '../../../service/posthog.service';
import { UserLoginService } from '../../../service/user-login.service';
import { GetTheftReportsOpts, RequestAccessQueryParams } from '../../../types';

type AccessType = 'theft-report';

@Component({
  selector: 'app-request-access-page',
  templateUrl: './request-access-page.component.html',
  styleUrls: ['./request-access-page.component.scss'],
  standalone: false,
})
export class RequestAccessPageComponent implements OnInit {
  public auth = inject(UserLoginService);
  private service = inject(PanoptycDataService);
  private route = inject(ActivatedRoute);
  private notifyService = inject(NotifyService);
  private posthogService = inject(PosthogService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);

  public accessType = signal<AccessType | undefined>(undefined);

  // used for theft reports
  public theftReportKey = signal<string>('');
  public displayName = signal<string>('');
  public branchId = signal<number>(0);
  public theftReportOpts = signal<GetTheftReportsOpts | undefined>(undefined);
  public prevUrl = signal<string>('');

  public isPendingRequestAccess = signal<boolean>(false);
  public hasRequestedAccess = signal<boolean>(false);
  public isCheckingAccess = signal<boolean>(false);
  public hasAccess = signal<boolean>(false);

  ngOnInit() {
    const queryParams = this.route.snapshot.queryParams as RequestAccessQueryParams;
    this.accessType.set(queryParams.type);
    this.displayName.set(queryParams.displayName);
    this.theftReportKey.set(queryParams.theftReportKey);
    this.branchId.set(Number(queryParams.branchId));
    this.prevUrl.set(queryParams.prev_url);

    const opts = {} as GetTheftReportsOpts;
    if (queryParams.sort_by) opts.sort_by = queryParams.sort_by;
    if (queryParams.sort_direction) opts.sort_direction = queryParams.sort_direction;
    if (queryParams.case_id) opts.case_id = queryParams.case_id;
    if (queryParams.date_shared_from) opts.date_shared_from = queryParams.date_shared_from;
    if (queryParams.date_shared_to) opts.date_shared_to = queryParams.date_shared_to;
    if (Object.keys(opts).length > 0) {
      this.theftReportOpts.set(opts);
    }

    timer(5_000, 5_000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.hasAccess()) {
          this.checkTheftReportAccess();
        }
      });

    this.posthogService.capture('view-request-access-page', { $set: { content_branch_id: this.branchId() } });
  }

  checkTheftReportAccess() {
    if (!this.theftReportKey()) return;
    this.isCheckingAccess.set(true);
    this.service
      .checkAccessToTheftReport(this.theftReportKey())
      .pipe(tap(() => this.isCheckingAccess.set(false)))
      .subscribe((hasAccess) => {
        this.hasAccess.set(hasAccess);
        if (this.hasAccess()) {
          // a little delay so the user can see the success indicator
          setTimeout(() => {
            const queryParams = { ...(this.theftReportOpts() || {}) };
            this.router.navigate(['/shared/theft-report', this.theftReportKey()], { queryParams });
          }, 500);
        }
      });
  }

  requestAccess() {
    this.isPendingRequestAccess.set(true);
    this.service.requestAccessToTheftReport(this.theftReportKey()).subscribe(
      () => {
        this.isPendingRequestAccess.set(false);
        this.hasRequestedAccess.set(true);
        this.notifyService.notify('Successfully requested access');
      },
      (err) => {
        this.isPendingRequestAccess.set(false);
        this.notifyService.notify(`Failed to request access: ${tryGetErrorMessage(err)}`);
      },
    );
  }

  switchAccount() {
    sessionStorage.setItem('post_login_redirect', this.prevUrl() ?? '/');
    this.auth.logoutAndRedirectToLogin();
  }
}
